<script>
import wait from 'linna-util/wait'

import DebugControls from './DebugControls'
import MainControls from './MainControls'

import GroundModel from './GroundModel'
import GroundPlane from './GroundPlane'
import Inventory from './Inventory'
import LevelMap from './LevelMap'
import LevelScene from './LevelScene'
import NodeCursor from './NodeCursor'
import PlayerCharacter from './PlayerCharacter'
import Sky from './Sky'

import animate from '@/util/animate'

export default {

  components: {
    DebugControls,
    GroundModel,
    GroundPlane,
    Inventory,
    LevelMap,
    LevelScene,
    MainControls,
    NodeCursor,
    PlayerCharacter,
    Sky
  },

  data () {
    return {
      renderedPlayerDirection: 0
    }
  },

  computed: {

    // Cursor

    cursorNodePosition () {
      return [
        this.$store.getters.cursorNode.renderedX,
        this.$store.getters.cursorNode.renderedY,
        1
      ]
    },

    // Player

    playerCoordinates () {
      return [this.$store.getters.playerNode.x, this.$store.getters.playerNode.y]
    },

    playerPosition () {
      return [
        this.$store.getters.playerNode.renderedX,
        this.$store.getters.playerNode.renderedY,
        this.$store.getters.playerNode.renderedZ
      ]
    },

    renderedPlayerPosition () {
      return [
        this.$store.getters.renderedPlayerX,
        this.$store.getters.renderedPlayerY,
        this.$store.getters.playerNode.renderedZ
      ]
    },

    playerRotation () {
      return [0, 180 + this.$store.getters.playerDirection, 0]
    }

  },

  watch: {

    // Visibly move player to match new coordinates
    async playerPosition ([playerX, playerY]) {
      if (this.$store.getters.renderedPlayerX !== playerX || this.$store.getters.renderedPlayerY !== playerY) {
        await this.movePlayerTo(playerX, playerY)

        this.onMoveComplete()
      }
    },

    '$store.getters.playerDirectionToCursor' (playerDirection) {
      if (this.$store.getters.playerDirection !== playerDirection) {
        this.animatePlayerDirectionTo(playerDirection)
      }
    }

  },

  methods: {

    // Level lifecycle

    onTurnStart () {
      console.log('Turn started')
    },

    onMoveComplete () {

      // Pick up items
      if (this.$store.getters.playerNode.itemIds.length) {
        this.$store.getters.playerNode.itemIds.forEach((itemId) => {
          this.$store.dispatch('collectItem', itemId)
        })
      }

      // Mark turn ended
      this.onTurnEnd()

      // Completed level
      if (this.$store.getters.playerNodeId === this.$store.getters.currentLevelGoalNodeId) {
        this.onLevelComplete()

      // FIXME: turn shouldn't end yet if player can fire?
      } else {
        this.onTurnStart()
      }

    },

    onTurnEnd () {
      console.log('Turn ended')
      this.$store.dispatch('markTurnCompleted')
    },

    onLevelComplete () {

      // Mark progress
      this.$store.dispatch('markLevelCompleted', this.$store.getters.currentLevelId)

      // Go to next level
      if (this.$store.getters.nextLevelId) {

        // Go to next level
        // this.$store.dispatch('clearCurrentLevelId')
        this.$router.push({
          name: 'level',
          params: {
            levelId: this.$store.getters.nextLevelId
          }
        })

      } else {

        // Go to main menu
        // Clear current level ID as we don't intend to coming back
        this.$store.dispatch('clearCurrentLevelId')
        this.$router.push({
          name: 'mainMenu'
        })

      }

    },



    // Interaction

    async movePlayerTo (x, y) {
      const cameraFov = this.$store.getters.cameraFov
      const cameraZoom = !!this.$store.getters.cameraZoom

      if (!this.$store.getters.playerCameraIsActive && this.$store.getters.cameraFollowsPlayer) {
        await Promise.all([
          this.$store.dispatch('animateCameraFovTo', cameraFov * 0.85),
          this.$store.dispatch('animateCameraZoomTo', cameraZoom * 1.15)
        ])
      }

      const moveAnimation = this.animatePlayerPositionTo(x, y)

      if (!this.$store.getters.playerCameraIsActive && this.$store.getters.cameraFollowsPlayer) {

        // Launch animation in time to complete when movement completes
        // FIXME: magic numbers
        await wait(600 - 200)

        this.$store.dispatch('animateCameraFovTo', cameraFov)
        this.$store.dispatch('animateCameraZoomTo', cameraZoom)
      }

      await moveAnimation

      return true
    },

    async animatePlayerPositionTo (x, y) {
      return animate(600, [this.$store.getters.renderedPlayerX, this.$store.getters.renderedPlayerY], [x, y], ([newX, newY]) => {

        if (this.$store.getters.renderedPlayerX !== newX) {
          this.$store.dispatch('setRenderedPlayerX', newX)
        }

        if (this.$store.getters.renderedPlayerY !== newY) {
          this.$store.dispatch('setRenderedPlayerY', newY)
        }

      })
    },

    async animatePlayerDirectionTo (playerDirection) {
      return animate(600, this.$store.getters.playerDirection, playerDirection, (frameDeg) => {
        if (this.$store.getters.playerDirection !== frameDeg) {
          this.$store.dispatch('setPlayerDirection', frameDeg)
        }
      })
    }

  }

}
</script>

<template>
  <div class="c-level-container">

    <main-controls class="c-level-container-main-controls" />

    <debug-controls class="c-level-container-debug-controls" />

    <inventory
      v-if="$store.getters.collectedItems.length"
      class="c-level-container-inventory"
    />

    <level-scene
      ref="levelScene"
      class="c-level-container-frame"
    >

      <a-entity
        ref="mapElements"
        position
      >

        <level-map
          ref="levelMap"
          :level="$store.getters.currentLevel"
        />

        <node-cursor
          ref="nodeCursor"
          :position="cursorNodePosition.join(' ')"
        />

        <a-entity
          :position="renderedPlayerPosition.join(' ')"
          rotation="90 0 0"
        >
          <player-character
            ref="playerCharacter"
            :rotation="playerRotation.join(' ')"
          />
        </a-entity>

      </a-entity>

      <a-entity
        v-if="$store.getters.currentLevel.terrainModel"
        position="0 0 -0.75"
        rotation="90 0 0"
      >
        <ground-model
          ref="groundModel"
          :rotation="[0, $store.getters.currentLevel.terrainRotation, 0].join(' ')"
          :model="$store.getters.currentLevel.terrainModel"
        />
      </a-entity>

      <ground-plane
        v-else
        ref="groundPlane"
        :x="$store.getters.currentLevel.maxX + 1"
        :y="$store.getters.currentLevel.maxY + 1"
      />

      <sky
        position="0 0 0"
        :time-of-day="$store.getters.currentLevel.timeOfDay"
      />

    </level-scene>
  </div>
</template>

<style lang="scss">

.c-level-container {
  position: relative;
}

.c-level-container-main-controls,
.c-level-container-debug-controls,
.c-level-container-inventory,
.c-level-container-frame {
  position: absolute;
}

.c-level-container-inventory {
  left: 1em;
  top: 1em;
  z-index: 2;
}

.c-level-container-main-controls {
  left: 1em;
  bottom: 1em;
  z-index: 3;
}

.c-level-container-debug-controls {
  right: 1em;
  bottom: 1em;
  z-index: 4;
}

.c-level-container-frame {
  z-index: 1;
}

</style>
